import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { CampaignLevelRow } from "../../shared/types/database";
import { getEnemySpritePath } from "../battle/assets/characterSprites";
import { getCampaignLevelState, localizedEnemyName } from "./campaignProgress";

interface CampaignLevelPickerProps {
  completedLevels: readonly number[];
  currentLevel: number;
  isUzbek: boolean;
  levels: readonly CampaignLevelRow[];
  onSelect: (level: CampaignLevelRow) => void;
  selectedLevel: number;
}

function CampaignStateIcon({ state }: { state: "completed" | "current" | "locked" }) {
  if (state === "completed") {
    return (
      <svg viewBox="0 0 24 24" data-state-icon="completed">
        <path d="M12 2.8 19 5.6v5.6c0 4.4-2.8 8.1-7 10-4.2-1.9-7-5.6-7-10V5.6L12 2.8Z" />
        <path d="m8.6 11.8 2.1 2.1 4.8-5" />
      </svg>
    );
  }

  if (state === "current") {
    return (
      <svg viewBox="0 0 24 24" data-state-icon="current">
        <path d="m5 4 6.1 6.1M3.8 3.1l2.6.7.7 2.6-2.4 2.4-1.8-1.8 2.4-2.4Z" />
        <path d="m19 4-6.1 6.1m7.3-7-2.6.7-.7 2.6 2.4 2.4 1.8-1.8-2.4-2.4Z" />
        <path d="m9.4 11.8-5 5 .1 2.7 2.7.1 5-5m2.4-2.8 5 5-.1 2.7-2.7.1-5-5" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" data-state-icon="locked">
      <rect x="5" y="10" width="14" height="11" rx="3" />
      <path d="M8.2 10V7.2a3.8 3.8 0 0 1 7.6 0V10M12 14.2v2.6" />
    </svg>
  );
}

export function CampaignLevelPicker({
  completedLevels,
  currentLevel,
  isUzbek,
  levels,
  onSelect,
  selectedLevel
}: CampaignLevelPickerProps) {
  const { t } = useTranslation();
  const railRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const rail = railRef.current;
    const selected = rail?.querySelector<HTMLElement>(
      `[data-campaign-level="${selectedLevel}"]`
    );
    if (!rail || !selected) {
      return;
    }

    const targetLeft = selected.offsetLeft - (rail.clientWidth - selected.clientWidth) / 2;
    rail.scrollTo({ left: Math.max(0, targetLeft), behavior: "smooth" });
  }, [levels.length, selectedLevel]);

  return (
    <div className="campaign-picker">
      <div className="campaign-picker__summary">
        <span>{t("campaignProgress")}</span>
        <strong>{t("campaignProgressValue", {
          current: Math.min(currentLevel, 20),
          total: 20
        })}</strong>
      </div>
      <div className="campaign-rail" ref={railRef}>
        {levels.map((level) => {
          const state = getCampaignLevelState(
            level.level_no,
            currentLevel,
            completedLevels
          );
          const selected = level.level_no === selectedLevel;
          return (
            <button
              className={`campaign-level campaign-level--${state}`}
              data-campaign-level={level.level_no}
              type="button"
              key={level.level_no}
              disabled={state === "locked"}
              aria-pressed={selected}
              aria-label={`${t("campaignLevel", { level: level.level_no })}: ${localizedEnemyName(level, isUzbek)}`}
              onClick={() => onSelect(level)}
            >
              <span className="campaign-level__number">{level.level_no}</span>
              <img
                className="campaign-enemy"
                src={getEnemySpritePath(level.enemy_variant, "idle")}
                alt=""
                draggable={false}
                aria-hidden="true"
              />
              <strong>{localizedEnemyName(level, isUzbek)}</strong>
              <small className="campaign-level__stats">
                <span>{level.opponent_hp} HP</span>
                {level.is_boss || level.enemy_tier > 1 ? (
                  <span>{level.is_boss ? t("campaignBoss") : t("campaignTier", {
                    tier: level.enemy_tier
                  })}</span>
                ) : null}
              </small>
              <span
                className={`campaign-level__state campaign-level__state--${state}`}
                aria-hidden="true"
              >
                <CampaignStateIcon state={state} />
              </span>
            </button>
          );
        })}
      </div>
      <p className="campaign-picker__hint">{t("campaignSwipeHint")}</p>
    </div>
  );
}
